import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import axios from 'axios';

interface PosConfig {
  id: string;
  provider: string;
  terminalId: string;
  merchantId: string;
  apiKey: string;
  isActive: boolean;
}

const POS_PROVIDERS = ['PineLabs', 'Paytm POS', 'Razorpay POS'];

const PosSettings: React.FC = () => {
  const [configs, setConfigs] = useState<PosConfig[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState<PosConfig | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  
  const [formData, setFormData] = useState({
    provider: '',
    terminalId: '',
    merchantId: '',
    apiKey: '',
    isActive: false,
  });

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const response = await axios.get('/system-settings/payments/pos');
      setConfigs(response.data);
    } catch (error) {
      console.error('Failed to fetch POS configs', error);
    }
  };

  const handleOpenDialog = (config?: PosConfig) => {
    if (config) {
      setEditingConfig(config);
      setFormData({
        provider: config.provider,
        terminalId: config.terminalId,
        merchantId: config.merchantId,
        apiKey: '', // Don't pre-fill secret
        isActive: config.isActive,
      });
    } else {
      setEditingConfig(null);
      setFormData({
        provider: '',
        terminalId: '',
        merchantId: '',
        apiKey: '',
        isActive: false,
      });
    }
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingConfig) {
        const payload: any = { ...formData };
        if (!payload.apiKey) delete payload.apiKey;

        await axios.patch(`/system-settings/payments/pos/${editingConfig.id}`, payload);
      } else {
        await axios.post('/system-settings/payments/pos', formData);
      }
      fetchConfigs();
      setOpenDialog(false);
    } catch (error) {
      console.error('Failed to save POS config', error);
      alert('Failed to save configuration.');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this POS configuration?')) {
      try {
        await axios.delete(`/system-settings/payments/pos/${id}`);
        fetchConfigs();
      } catch (error: any) {
        alert(error.response?.data?.message || 'Failed to delete configuration');
      }
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      if (currentStatus) {
        await axios.patch(`/system-settings/payments/pos/${id}/deactivate`);
      } else {
        await axios.patch(`/system-settings/payments/pos/${id}/activate`);
      }
      fetchConfigs();
    } catch (error) {
      console.error('Failed to toggle status', error);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">POS Configurations</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add POS Terminal
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Provider</TableCell>
              <TableCell>Terminal ID</TableCell>
              <TableCell>Merchant ID</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configs.map((config) => (
              <TableRow key={config.id}>
                <TableCell>{config.provider}</TableCell>
                <TableCell>{config.terminalId}</TableCell>
                <TableCell>{config.merchantId}</TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.isActive}
                        onChange={() => handleToggleActive(config.id, config.isActive)}
                      />
                    }
                    label={config.isActive ? 'Active' : 'Inactive'}
                  />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpenDialog(config)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(config.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {configs.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No POS terminals configured
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingConfig ? 'Edit POS Terminal' : 'Add POS Terminal'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info">
                Configure your physical Point of Sale (POS) terminal details here.
              </Alert>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Provider"
                value={formData.provider}
                onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
              >
                {POS_PROVIDERS.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Terminal ID"
                value={formData.terminalId}
                onChange={(e) => setFormData({ ...formData, terminalId: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Merchant ID"
                value={formData.merchantId}
                onChange={(e) => setFormData({ ...formData, merchantId: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type={showSecret ? 'text' : 'password'}
                label="API Key"
                value={formData.apiKey}
                placeholder={editingConfig ? 'Leave empty to keep existing' : ''}
                onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowSecret(!showSecret)}>
                      {showSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Set as Active Terminal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PosSettings;
